import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Chip,
  OutlinedInput
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAvailableTeamLeads, fetchAvailableAssistantManagers, fetchStates, fetchDiseases } from '../../store/slices/usersSlice';

const UserForm = ({ open, onClose, onSubmit, user = null, loading = false }) => {
  const isEdit = Boolean(user);
  const dispatch = useDispatch();
  const { states, diseases, availableTeamLeads, availableAssistantManagers } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);
  
  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      username: user?.username || '',
      email: user?.email || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || '',
      role: user?.role || 'bda',
      is_active: user?.is_active !== undefined ? user.is_active : true,
      password: '',
      confirm_password: '',
      manager_id: user?.manager_id || null,
      team_lead: user?.team_lead || null,
      assistant_manager: user?.assistant_manager || null,
      assigned_states: user?.assigned_states?.map(state => state.id) || [],
      assigned_diseases: user?.assigned_diseases?.map(disease => disease.id) || []
    }
  });

  const password = watch('password');
  const selectedRole = watch('role');

  // Role-based restrictions
  const allowedRoles = useMemo(() => {
    if (currentUser?.role === 'manager') {
      return [
        { value: 'assistant_manager', label: 'Assistant Manager' },
        { value: 'team_lead', label: 'Team Lead' },
        { value: 'bda', label: 'BDA' }
      ];
    }
    if (currentUser?.role === 'assistant_manager') {
      return [
        { value: 'team_lead', label: 'Team Lead' },
        { value: 'bda', label: 'BDA' }
      ];
    }
    if (currentUser?.role === 'team_lead') {
      return [
        { value: 'bda', label: 'BDA' }
      ];
    }
    // Admin can create all roles
    return [
      { value: 'admin', label: 'Admin' },
      { value: 'manager', label: 'Manager' },
      { value: 'assistant_manager', label: 'Assistant Manager' },
      { value: 'team_lead', label: 'Team Lead' },
      { value: 'bda', label: 'BDA' }
    ];
  }, [currentUser?.role]);

  // Fetch required data when form opens
  useEffect(() => {
    if (open) {
      // Always fetch states and diseases when form opens
      if (!states.length) {
        dispatch(fetchStates());
      }
      if (!diseases.length) {
        dispatch(fetchDiseases());
      }

      // Fetch available team leads for BDA users
      if (selectedRole === 'bda' && (currentUser?.role === 'manager' || currentUser?.role === 'assistant_manager')) {
        dispatch(fetchAvailableTeamLeads());
      }

      // Fetch available assistant managers when creating a team lead as manager or assistant_manager
      if (selectedRole === 'team_lead' && (currentUser?.role === 'manager' || currentUser?.role === 'assistant_manager')) {
        dispatch(fetchAvailableAssistantManagers());
      }
    }
  }, [open, selectedRole, currentUser?.role, dispatch, states.length, diseases.length, user]);

  // Reset form when user data changes
  useEffect(() => {
    if (user) {
      const formData = {
        username: user.username || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        role: user.role || 'bda',
        is_active: user.is_active !== undefined ? user.is_active : true,
        password: '',
        confirm_password: '',
        manager_id: user.manager_id || null,
        team_lead: user.team_lead || null,
        assistant_manager: user.assistant_manager || null,
        assigned_states: user.assigned_states?.map(state => state.id) || [],
        assigned_diseases: user.assigned_diseases?.map(disease => disease.id) || []
      };
      reset(formData);
    } else {
      // Reset to default values for new user
      reset({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'bda',
        is_active: true,
        password: '',
        confirm_password: '',
        manager_id: null,
        team_lead: null,
        assistant_manager: null,
        assigned_states: [],
        assigned_diseases: []
      });
    }
  }, [user, reset]);

  const handleFormSubmit = (data) => {
    const submitData = { ...data };
    
    // Remove password fields if empty (for edit mode)
    if (isEdit && !data.password) {
      delete submitData.password;
      delete submitData.confirm_password;
    }

    // Strip role-irrelevant fields to avoid backend validation errors
    if (submitData.role === 'bda') {
      // BDA has team_lead, not assistant_manager
      delete submitData.assistant_manager;
    } else if (submitData.role === 'team_lead') {
      // Team Lead has assistant_manager, not team_lead
      delete submitData.team_lead;
    } else {
      // manager, assistant_manager, admin have neither
      delete submitData.team_lead;
      delete submitData.assistant_manager;
    }

    // Clean up remaining empty optional fields
    if ('team_lead' in submitData && !submitData.team_lead) delete submitData.team_lead;
    if ('assistant_manager' in submitData && !submitData.assistant_manager) delete submitData.assistant_manager;
    
    onSubmit(submitData);
    if (!isEdit) {
      reset();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };



  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit User' : 'Create New User'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="first_name"
                control={control}
                rules={{ required: 'First name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="last_name"
                control={control}
                rules={{ required: 'Last name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="username"
                control={control}
                rules={{ required: 'Username is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Username"
                    fullWidth
                    error={!!errors.username}
                    helperText={errors.username?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                rules={{ 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>

            {/* Role and Permissions */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" sx={{ mb: 1, mt: 2 }}>
                Role & Permissions
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="role"
                control={control}
                rules={{ required: 'Role is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.role}>
                    <InputLabel>Role</InputLabel>
                    <Select {...field} label="Role">
                      {allowedRoles.map((role) => (
                        <MenuItem key={role.value} value={role.value}>
                          {role.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            
            {/* Team Lead Assignment for BDA users */}
            {selectedRole === 'bda' && (currentUser?.role === 'manager' || currentUser?.role === 'assistant_manager') && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="team_lead"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Assign Team Lead</InputLabel>
                      <Select {...field} label="Assign Team Lead" value={field.value || ''}>
                        <MenuItem value="">
                          <em>No Team Lead</em>
                        </MenuItem>
                        {Array.isArray(availableTeamLeads) && availableTeamLeads.length > 0 ? (
                          availableTeamLeads.map((teamLead) => (
                            <MenuItem key={teamLead.id} value={teamLead.id}>
                              {teamLead.first_name} {teamLead.last_name} ({teamLead.username})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            <em>No team leads available</em>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            {/* Assistant Manager Assignment for Team Lead (optional) */}
            {selectedRole === 'team_lead' && (currentUser?.role === 'manager' || currentUser?.role === 'assistant_manager') && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="assistant_manager"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Assign Assistant Manager (Optional)</InputLabel>
                      <Select {...field} label="Assign Assistant Manager (Optional)" value={field.value || ''}>
                        <MenuItem value="">
                          <em>None (reports directly to Manager)</em>
                        </MenuItem>
                        {Array.isArray(availableAssistantManagers) && availableAssistantManagers.length > 0 ? (
                          availableAssistantManagers.map((am) => (
                            <MenuItem key={am.id} value={am.id}>
                              {am.first_name} {am.last_name} ({am.username})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            <em>No assistant managers available</em>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        color="primary"
                      />
                    }
                    label="Active User"
                  />
                )}
              />
            </Grid>

            {/* Assignments Section */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" sx={{ mb: 1, mt: 2 }}>
                Territory & Disease Assignments
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="assigned_states"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Assigned States</InputLabel>
                    <Select
                      {...field}
                      multiple
                      input={<OutlinedInput label="Assigned States" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((stateId) => {
                            const state = Array.isArray(states) ? states.find(s => s.id === stateId) : null;
                            return (
                              <Chip
                                key={stateId}
                                label={state ? state.name : stateId}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {Array.isArray(states) && states.length > 0 ? (
                        states.map((state) => (
                          <MenuItem key={state.id} value={state.id}>
                            {state.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          <em>No states available</em>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="assigned_diseases"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Assigned Diseases</InputLabel>
                    <Select
                      {...field}
                      multiple
                      input={<OutlinedInput label="Assigned Diseases" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((diseaseId) => {
                            const disease = Array.isArray(diseases) ? diseases.find(d => d.id === diseaseId) : null;
                            return (
                              <Chip 
                                key={diseaseId} 
                                label={disease ? disease.name : diseaseId}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {Array.isArray(diseases) && diseases.length > 0 ? (
                        diseases.map((disease) => (
                          <MenuItem key={disease.id} value={disease.id}>
                            {disease.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          <em>No diseases available</em>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Password Section */}
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" sx={{ mb: 1, mt: 2 }}>
                {isEdit ? 'Change Password (leave blank to keep current)' : 'Password'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="password"
                control={control}
                rules={!isEdit ? { required: 'Password is required' } : {}}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type="password"
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="confirm_password"
                control={control}
                rules={{
                  validate: value => {
                    if (!isEdit && !value) return 'Please confirm password';
                    if (password && value !== password) return 'Passwords do not match';
                    return true;
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    error={!!errors.confirm_password}
                    helperText={errors.confirm_password?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;